import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import Container from 'web/components/Container';
import { AnchorStyled, LinkStyled } from 'web/components/elements';
import LazyImg from 'web/components/LazyImg';
import Meta from 'web/components/Meta';
import useErrorHandler from 'web/hooks/useErrorHandler';
import useInitialRouteData from 'web/hooks/useInitialRouteData';
import useQueryCache from 'web/hooks/useQueryCache';
import useSanityImageUrlBuilder from 'web/hooks/useSanityImageUrlBuilder';
import useSanityQuery from 'web/hooks/useSanityQuery';
import themeConstants from 'web/styles/themeConstants';
import { FakeText, SubTitle, Title } from './common';
import PostsList, { PostsListLoading } from './PostsList';
import { Author, Post } from './types';

const blogAuthorQuery = `*[_type == "blogAuthor" && slug.current == $slug][0] {
  _id,
  name,
  bio,
  "photo": {
    "_type": photo._type,
    "asset": photo.asset,
    "metadata": photo.asset->metadata{
      lqip
    }
  },
  websiteUrl,
  "posts": *[_type == "blogPost" && references(^._id)] | order(publishedAt desc) {
    _id,
    slug,
    title,
    description,
    publishedAt,
    "coverImage": {
      "_type": coverImage._type,
      "alt": coverImage.alt,
      "asset": coverImage.asset,
      "metadata": coverImage.asset->metadata{
        lqip
      }
    },
    "readTime": round(length(pt::text(body)) / 5 / 180)
  }
}`;

const AuthorGrid = styled.div`
  display: grid;
  margin-top: 48px;
  margin-bottom: 40px;
  ${themeConstants.media.md} {
    grid-template-columns: min-content auto;
    column-gap: 40px;
    ${Title} {
      margin-top: 0;
    }
    ${SubTitle} {
      margin-bottom: 0;
    }
  }
`;

const AuthorPhoto = styled.div`
  justify-self: center;
  > img {
    border-radius: 50%;
  }
`;

const BlogAuthor = () => {
  const { slug } = useParams();
  const sanityParams = useMemo(() => ({ slug }), [slug]);
  const initialData = useInitialRouteData<Author & { posts: Post[] }>();
  const result = useSanityQuery<Author & { posts: Post[] }>(blogAuthorQuery, sanityParams);
  const [author, loading, error] = useQueryCache(`blog:author:${slug}`, result, initialData);
  useErrorHandler(error);
  const imgUrl = useSanityImageUrlBuilder();

  return (
    <Container style={{ marginBottom: 80 }}>
      <nav>
        <LinkStyled to="/blog">Introwise Blog</LinkStyled>
      </nav>
      {loading && (
        <>
          <FakeText as="h1">Loading...</FakeText>
          <FakeText as="p">Loading...</FakeText>
          <PostsListLoading />
        </>
      )}
      {!loading && error && <>{`${error}`}</>}
      {!loading && !error && !author && (
        <>
          <Meta title={`Page not found – Introwise Blog`} />
          <Title>Not found</Title>
          <SubTitle>
            The page you are looking for doesn&apos;t exist. But you might find something interesting in{' '}
            <LinkStyled to="/blog">our blog</LinkStyled>.
          </SubTitle>
        </>
      )}
      {!loading && !error && author && (
        <>
          <Meta title={`${author.name} – Introwise Blog`} description={author.bio} />
          <AuthorGrid>
            <AuthorPhoto>
              <LazyImg
                lqip={author.photo.metadata.lqip}
                src={imgUrl(author.photo).width(200).height(200).url()}
                width={200}
                height={200}
                alt={author.name}
              />
            </AuthorPhoto>
            <div>
              <Title>{author.name}</Title>
              <SubTitle as="div">
                <p>{author.bio}</p>
                {author.websiteUrl && (
                  <p>
                    <AnchorStyled target="_blank" rel="noopener noreferrer" href={author.websiteUrl}>
                      {author.websiteUrl.replace(/https?:\/\//, '')}
                    </AnchorStyled>
                  </p>
                )}
              </SubTitle>
            </div>
          </AuthorGrid>
          <PostsList posts={author.posts} />
        </>
      )}
    </Container>
  );
};

export default BlogAuthor;
